import { useActiveProject } from "@/utils/utils";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useCustomKnowledge } from "../../hooks/useCustomKnowledge";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useSearchParams } from "react-router";

export default function CustomKnowledge() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project_id");
  const [showResetPromptModal, setShowResetPromptModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const project = useActiveProject();

  const resolvedProjectId = project?.id || projectId;
  const { currentPrompt, savePrompt, resetPrompt } =
    useCustomKnowledge(resolvedProjectId);

  const [localPrompt, setLocalPrompt] = useState(currentPrompt);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    setLocalPrompt(currentPrompt);
    setHasUnsavedChanges(false);
  }, [currentPrompt]);

  const handleChange = (e) => {
    setLocalPrompt(e.target.value);
    setHasUnsavedChanges(e.target.value !== currentPrompt);
  };

  const handleSave = async () => {
    const id = resolvedProjectId;
    if (!id || isProcessing) return;
    setIsProcessing(true);
    try {
      const success = await savePrompt(localPrompt);
      if (!success) throw new Error("Failed to save custom knowledge");
      toast.success("Custom knowledge saved successfully");
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error(`Error saving custom knowledge: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetClick = () => {
    if (!isProcessing) {
      setShowResetPromptModal(true);
    }
  };

  const handleReset = async () => {
    setShowResetPromptModal(false);
    const id = resolvedProjectId;
    if (!id) {
      toast.error("Project not found");
      return;
    }
    try {
      const success = await resetPrompt();
      if (!success) {
        throw new Error("Failed to reset custom knowledge");
      }
      toast.success("Custom knowledge reset successfully");
      setLocalPrompt("");
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error(`Error resetting custom knowledge: ${error.message}`);
    }
  };

  return (
    <div className="flex flex-col gap-4 px-4">
      {showResetPromptModal && (
        <Dialog
          open={showResetPromptModal}
          onOpenChange={setShowResetPromptModal}
        >
          <DialogContent className="border border-secondary">
            <DialogHeader>
              <DialogTitle className="-mx-6 px-6 pb-4 border-b font-medium border-secondary">
                Reset to Default
              </DialogTitle>
              <DialogDescription className="pt-2 text-gray-200">
                Are you sure you want to reset to default? You'll permanently
                lose all of your changes, and this cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="-mx-6 px-6 pt-[16px] border-t border-secondary">
              <Button
                variant="secondary"
                className="rounded-lg"
                onClick={() => setShowResetPromptModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReset}
                variant="primary"
                className="rounded-lg"
              >
                Reset
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <div className="flex flex-col gap-1">
        <p className="text-xs font-medium text-secondary-n-50">
          Customize Tempo's system prompt
        </p>
        <p className="text-2xs text-secondary-n-400">
          Add specific instructions, context or documentation for the custom
          Tempo's AI to follow.
        </p>
      </div>

      <div className="flex flex-col gap-2 group">
        <Textarea
          value={localPrompt}
          onChange={handleChange}
          placeholder="Use #131519 for dark mode"
          className={cn(
            "w-full min-h-[96px] text-[12px] p-2 resize-none bg-[#1a1b1d] text-white placeholder:text-secondary-600 rounded-lg border hover:border-secondary-n-600 group-focus-within:border-primary-n-600"
          )}
        />

        {hasUnsavedChanges && (
          <Button
            onClick={handleSave}
            variant="primary"
            className="rounded-md w-full text-xs h-8 font-medium"
            disabled={isProcessing}
          >
            Save
          </Button>
        )}

        {localPrompt && !hasUnsavedChanges && (
          <Button
            onClick={handleResetClick}
            variant="outline"
            className="rounded-md w-full flex items-center justify-center gap-2 text-xs h-8 font-medium  bg-transparent hover:bg-secondary-n-950 text-white"
            disabled={isProcessing}
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "var(--secondary-n-900)",
            }}
          >
            Reset to default
          </Button>
        )}
      </div>
    </div>
  );
}
